import { useQuery } from "@origin-digital/query";
import { fetchSearchResults } from "../../../services/search";
import {
  toError,
  useShouldThrowQueryError,
} from "../../../hooks/useShouldThrowQueryError";
import type {
  ILabelQueryDto,
  ISearchResponseDto,
} from "../../../contract/generated/bff/src/models";

export interface UseSearchResultsResult {
  data: ISearchResponseDto | null;
  relatedSearches: ILabelQueryDto[];
  spellingSuggestion: ILabelQueryDto | null;
  loading: boolean;
  error: Error | null;
}

export const useSearchResults = (
  term: string | null,
  filter?: string,
  page: number = 1
): UseSearchResultsResult => {
  const {
    data: searchData,
    error: searchError,
    isLoading: searchLoading,
  } = useQuery({
    queryKey: ["support-search", term, filter, page],
    queryFn: () =>
      fetchSearchResults({
        q: term as string, // safe: query only runs when term is non-null (see enabled)
        filter,
        page,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: Boolean(term),
  });

  const resolvedSearchError = toError(searchError);
  useShouldThrowQueryError(resolvedSearchError, {
    component: "SearchResultsPage",
    action: "fetchSearchResults",
  });

  return {
    data: searchData?.data ?? null,
    relatedSearches: searchData?.data?.relatedSearches ?? [],
    spellingSuggestion: searchData?.data?.spellingSuggestion ?? null,
    loading: searchLoading,
    error: resolvedSearchError,
  };
};
